import React, { useMemo } from 'react';
import { C, FONT_BODY } from '../theme';
import { classifyAllExercises4State, buildExerciseInsights, filterWarmupSets } from '../science';
import { findExercise } from '../exerciseDB';
import { _globalSettings } from '../constants';

function WeightTrend({ ex, allSessions }) {
  const history = useMemo(() => {
    const name = ex.name.toLowerCase().trim();
    const past = allSessions
      .filter(s => s.exercises.some(e => e.name.toLowerCase().trim() === name))
      .sort((a, b) => a.date.localeCompare(b.date))
      .slice(-4);
    return past.map(s => {
      const e = s.exercises.find(e => e.name.toLowerCase().trim() === name);
      const working = filterWarmupSets(e.sets || []);
      const maxW = Math.max(0, ...working.map(s => parseFloat(s.weight) || 0));
      return { date: s.date, weight: maxW };
    }).filter(e => e.weight > 0);
  }, [ex, allSessions]);

  if (history.length < 2) return null;

  const weights = history.map(h => h.weight);
  const trend = weights[weights.length - 1] - weights[0];

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      <span style={{ fontSize: 13, color: C.text, fontWeight: 600, minWidth: 0, flex: '0 1 auto', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {ex.name}
      </span>
      <span style={{ fontSize: 12, color: C.subtle, fontFamily: FONT_BODY }}>
        {weights.join(' → ')} kg
      </span>
      {trend > 0 && <span style={{ fontSize: 11, fontWeight: 700, color: C.green, background: `${C.green}18`, borderRadius: 6, padding: '2px 7px' }}>+{trend.toFixed(1)} kg</span>}
      {trend < 0 && <span style={{ fontSize: 11, fontWeight: 700, color: C.red, background: `${C.red}18`, borderRadius: 6, padding: '2px 7px' }}>{trend.toFixed(1)} kg</span>}
      {trend === 0 && <span style={{ fontSize: 11, fontWeight: 700, color: C.amber, background: `${C.amber}18`, borderRadius: 6, padding: '2px 7px' }}>stallo</span>}
    </div>
  );
}

export function SessionAnalysisPanel({ session, allSessions }) {
  const states = useMemo(() => classifyAllExercises4State([...allSessions, session]), [session, allSessions]);

  const prescriptions = useMemo(() => {
    return session.exercises.map(ex => {
      const dbEx = findExercise(ex.name);
      const type = dbEx?.type || 'compound';
      const prevSess = [...allSessions]
        .sort((a, b) => b.date.localeCompare(a.date))
        .find(s => s.exercises.some(e => e.name.toLowerCase().trim() === ex.name.toLowerCase().trim()));
      const prevEx = prevSess?.exercises.find(e => e.name.toLowerCase().trim() === ex.name.toLowerCase().trim());
      const insights = buildExerciseInsights(
        ex.sets, type, prevEx?.sets, ex.name, allSessions,
        null, _globalSettings.repRange, { showRpe: false }
      );
      const p = insights.nextSessionPrescription;
      if (!p?.prescriptionNote) return null;
      return { name: ex.name, note: p.prescriptionNote };
    }).filter(Boolean);
  }, [session, allSessions]);

  const hasTrends = session.exercises.some(ex => {
    const name = ex.name.toLowerCase().trim();
    return allSessions.filter(s => s.exercises.some(e => e.name.toLowerCase().trim() === name)).length >= 1;
  });

  if (!hasTrends && !prescriptions.length) return null;

  return (
    <div style={{ margin: '16px 0', borderRadius: 16, background: 'rgba(120,120,128,0.06)', padding: '16px 16px 12px', border: '0.5px solid rgba(60,60,67,0.10)' }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: C.subtle, letterSpacing: 0.4, textTransform: 'uppercase', marginBottom: 14, fontFamily: FONT_BODY }}>
        Analisi sessione
      </div>

      {/* Trend pesi */}
      {hasTrends && (
        <div style={{ marginBottom: prescriptions.length ? 14 : 0 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: C.muted, marginBottom: 8, fontFamily: FONT_BODY }}>Trend pesi</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {session.exercises.map((ex, i) => {
              const st = states[ex.name];
              return (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'space-between', flexWrap: 'wrap' }}>
                  <WeightTrend ex={ex} allSessions={allSessions} />
                  {st && (
                    <span style={{ fontSize: 10, fontWeight: 700, color: st.color, background: `${st.color}18`, borderRadius: 6, padding: '2px 7px', whiteSpace: 'nowrap', flexShrink: 0 }}>
                      {st.state}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Prescrizioni prossima sessione */}
      {prescriptions.length > 0 && (
        <div>
          <div style={{ fontSize: 12, fontWeight: 600, color: C.muted, marginBottom: 8, fontFamily: FONT_BODY }}>Prossima sessione</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            {prescriptions.map((p, i) => (
              <div key={i} style={{ fontSize: 13, color: C.text, fontFamily: FONT_BODY }}>
                <span style={{ fontWeight: 600 }}>{p.name}:</span>{' '}
                <span style={{ color: C.subtle }}>{p.note}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
